// Constante para debounce de mudanças de quantidade
const ON_CHANGE_DEBOUNCE_TIMER = 300;

// Funções para persistência de quantidades no localStorage
const CartStorage = {
  STORAGE_KEY: 'cart_quantities',

  // Verifica se um item é um brinde/gift (não deve ser sincronizado)
  isGiftItem(item) {
    // Verifica por preço zero
    if (item.final_price === 0 || item.price === 0) return true;
    // Verifica por handle contendo gift ou brinde
    if (item.handle && (item.handle.includes('gift') || item.handle.includes('brinde') || item.handle.includes('docapp-free-gift'))) return true;
    // Verifica por propriedades de brinde
    if (item.properties && (item.properties._gift || item.properties._free_gift)) return true;
    return false;
  },

  // Salva as quantidades atuais do carrinho (usando variant_id como chave)
  saveQuantities() {
    const items = document.querySelectorAll('.cart-item');
    const quantities = {};
    
    items.forEach(item => {
      const input = item.querySelector('.quantity__input');
      if (input) {
        const variantId = input.dataset.quantityVariantId || input.id;
        // Usa variant ID como chave em vez de index
        if (variantId) {
          quantities[variantId] = {
            quantity: parseInt(input.value, 10)
          };
        }
      }
    });
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(quantities));
  },

  // Obtém as quantidades salvas
  getQuantities() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      return null;
    }
  },

  // Limpa as quantidades salvas
  clear() {
    localStorage.removeItem(this.STORAGE_KEY);
  },

  // Sincroniza as quantidades salvas com o servidor
  async syncWithServer() {
    const savedQuantities = this.getQuantities();
    if (!savedQuantities || Object.keys(savedQuantities).length === 0) return;

    // Busca o estado atual do carrinho do servidor
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Verifica se há diferenças entre o localStorage e o servidor
      let needsUpdate = false;
      const updates = {};
      
      cart.items.forEach((item) => {
        const variantId = item.variant_id.toString();
        const saved = savedQuantities[variantId];
        
        // Ignora brindes/gifts - não sincroniza quantidade
        if (this.isGiftItem(item)) return;
        
        if (saved && saved.quantity !== item.quantity) {
          needsUpdate = true;
          updates[item.key] = saved.quantity;
        }
      });

      // Se há diferenças, atualiza o servidor
      if (needsUpdate) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates })
        });
      }
    } catch (e) {
      console.error('Erro ao sincronizar carrinho:', e);
    }
  }
};

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    // Limpa localStorage antigo para evitar conflitos com apps de brindes
    CartStorage.clear();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    // Fecha o carrinho ao clicar fora do drawer__inner (na área escura/overlay)
    this.addEventListener('click', (evt) => {
      // Ignora se o clique foi em elementos interativos do carrinho
      if (evt.target.closest('cart-remove-button') || 
          evt.target.closest('quantity-input') || 
          evt.target.closest('button') ||
          evt.target.closest('a') ||
          evt.target.closest('input')) {
        return;
      }
      
      // Não fecha se o clique foi em qualquer elemento dentro do drawer__inner
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner && drawerInner.contains(evt.target)) {
        return; // Clique dentro do drawer, não fecha
      }
      
      // Fecha apenas se clicou fora do drawer (área escura)
      this.close();
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // Atualiza o conteúdo do carrinho ao abrir (sem sincronização para evitar conflitos com apps de brindes)
    this.refreshDrawerOnOpen();
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {this.classList.add('active')});

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.add('closing');
    this.classList.remove('active');
    
    this.addEventListener('transitionend', () => {
      this.classList.remove('closing');
      removeTrapFocus(this.activeElement);
    }, { once: true });
    
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') && this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section => {
      // Tratamento especial para cart-icon-bubble: atualiza innerHTML para preservar o <a>
      if (section.id === 'cart-icon-bubble') {
        const targetElement = document.getElementById(section.id);
        if (targetElement && parsedState.sections[section.id]) {
          targetElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id]);
        }
        return;
      }
      
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const newContent = this.getSectionDOM(parsedState.sections[section.id], section.selector);
      if (newContent && sectionElement) {
        const adoptedNode = document.adoptNode(newContent);
        sectionElement.replaceWith(adoptedNode);
      }
    }));

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  // Reinicializa o Disco.js em um elemento
  reinitializeDisco(element) {
    if (!element) return;
    
    // Tenta reinicializar usando diferentes métodos possíveis do Disco.js
    if (window.Disco && typeof window.Disco.init === 'function') {
      window.Disco.init(element);
    } else if (window.Disco && typeof window.Disco.start === 'function') {
      window.Disco.start(element);
    } else if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      window.Alpine.initTree(element);
    }
    
    // Dispara evento customizado para qualquer listener
    element.dispatchEvent(new CustomEvent('disco:init', { bubbles: true }));
  }

  // Método para atualizar o conteúdo do drawer via fetch
  refreshDrawer() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart-drawer__warnings', '.drawer__heading__container', '.free-shipping-message'];
        
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            const adoptedNode = document.adoptNode(sourceElement);
            targetElement.replaceWith(adoptedNode);
          }
        }

        // Atualiza a classe is-empty do cart-drawer, preservando as classes de estado
        const sourceCartDrawer = html.querySelector('cart-drawer');
        if (sourceCartDrawer) {
          const wasActive = this.classList.contains('active');
          const wasClosing = this.classList.contains('closing');
          this.className = sourceCartDrawer.className;
          if (wasActive) this.classList.add('active');
          if (wasClosing) this.classList.add('closing');
        }

        // Reinicializa o Disco.js no free-shipping-message atualizado
        const freeShippingMessage = document.querySelector('.free-shipping-message');
        this.reinitializeDisco(freeShippingMessage);
      })
      .catch((e) => {
        console.error('Erro ao atualizar cart-drawer:', e);
      });
  }

  // Método para atualizar o drawer ao abrir (atualiza tudo incluindo recomendações)
  refreshDrawerOnOpen() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        
        // Atualiza elementos específicos usando replaceWith para preservar Custom Elements
        const selectors = [
          'cart-drawer-items',
          '.drawer__recommendations',
          '.drawer__footer',
          '.cart-drawer__warnings',
          '.drawer__heading__container',
          '.free-shipping-message'
        ];
        
        for (const selector of selectors) {
          const targetElement = this.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            // Adota o nó no documento atual para garantir que Custom Elements sejam upgraded
            const adoptedNode = document.adoptNode(sourceElement);
            targetElement.replaceWith(adoptedNode);
          }
        }

        // Atualiza a classe is-empty do cart-drawer, preservando as classes de estado
        const sourceCartDrawer = html.querySelector('cart-drawer');
        if (sourceCartDrawer) {
          const wasActive = this.classList.contains('active');
          const wasClosing = this.classList.contains('closing');
          this.className = sourceCartDrawer.className;
          if (wasActive) this.classList.add('active');
          if (wasClosing) this.classList.add('closing');
        }

        // Atualiza o ícone do carrinho no header
        const targetBubble = document.getElementById('cart-icon-bubble');
        const sourceBubble = html.getElementById('cart-icon-bubble');
        if (targetBubble && sourceBubble) {
          targetBubble.innerHTML = sourceBubble.innerHTML;
        }

        // Reinicializa o slider de recomendações após atualização
        this.initRecommendationsSlider();

        // Reinicializa o Disco.js no free-shipping-message atualizado
        const freeShippingMessage = this.querySelector('.free-shipping-message');
        this.reinitializeDisco(freeShippingMessage);
      })
      .catch((e) => {
        console.error('Erro ao atualizar cart-drawer:', e);
      });
  }

  // Inicializa o slider de recomendações
  initRecommendationsSlider() {
    // Aguarda o DOM estar completamente renderizado
    setTimeout(() => {
      const carousel = this.querySelector('.recommendations-carousel');
      let prevBtn = this.querySelector('.recommendations-nav__prev');
      let nextBtn = this.querySelector('.recommendations-nav__next');
      
      if (!carousel || !prevBtn || !nextBtn) return;

      const cardWidth = 192; // 180px card + 12px gap
      
      // Remove listeners antigos clonando os botões
      const newPrevBtn = prevBtn.cloneNode(true);
      const newNextBtn = nextBtn.cloneNode(true);
      prevBtn.parentNode.replaceChild(newPrevBtn, prevBtn);
      nextBtn.parentNode.replaceChild(newNextBtn, nextBtn);
      
      // Atualiza referências para os novos botões
      prevBtn = newPrevBtn;
      nextBtn = newNextBtn;
      
      const updateNavButtons = () => {
        const scrollLeft = carousel.scrollLeft;
        const maxScroll = carousel.scrollWidth - carousel.clientWidth;
        
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
      };

      prevBtn.addEventListener('click', () => {
        carousel.scrollBy({ left: -cardWidth, behavior: 'smooth' });
      });

      nextBtn.addEventListener('click', () => {
        carousel.scrollBy({ left: cardWidth, behavior: 'smooth' });
      });

      carousel.addEventListener('scroll', updateNavButtons);
      
      // Atualizar estado inicial após imagens carregarem
      updateNavButtons();
      
      // Recalcula após as imagens carregarem
      const images = carousel.querySelectorAll('img');
      images.forEach(img => {
        if (!img.complete) {
          img.addEventListener('load', updateNavButtons);
        }
      });
    }, 100);
  }
}

customElements.define('cart-drawer', CartDrawer);

// Listener global para atualizar o cart-drawer quando um produto é adicionado
document.addEventListener('DOMContentLoaded', () => {
  // Flag para evitar loops infinitos com apps de brindes
  let isRefreshing = false;
  let refreshTimeout = null;
  
  // Intercepta o fetch para /cart/add e atualiza o drawer
  const originalFetch = window.fetch;
  window.fetch = function(...args) {
    return originalFetch.apply(this, args).then(response => {
      const url = args[0];
      
      // Verifica se é uma chamada de carrinho
      if (typeof url === 'string' && url.includes('/cart/add')) {
        // Evita múltiplos refreshes em sequência (debounce)
        if (refreshTimeout) {
          clearTimeout(refreshTimeout);
        }
        
        // Evita refresh se já está em processo de atualização
        if (!isRefreshing) {
          refreshTimeout = setTimeout(() => {
            isRefreshing = true;
            const cartDrawer = document.querySelector('cart-drawer');
            if (cartDrawer && typeof cartDrawer.refreshDrawer === 'function') {
              cartDrawer.refreshDrawer();
            }
            // Aguarda um tempo maior antes de permitir novo refresh
            setTimeout(() => {
              isRefreshing = false;
            }, 500);
          }, 300);
        }
      }
      return response;
    });
  };
});

class CartDrawerItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    this.setupEventListeners();
  }

  setupEventListeners() {
    // Debounce apenas para a requisição ao servidor
    this.debouncedServerUpdate = debounce((index, value, name, variantId) => {
      this.updateQuantity(index, value, name, variantId);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    // Listener que atualiza UI imediatamente e servidor com debounce
    this.addEventListener('change', this.handleChange.bind(this));
    
    // Também escuta eventos de input para atualização visual mais responsiva
    this.addEventListener('input', this.handleInput.bind(this));
  }

  handleChange(event) {
    if (!event.target.classList.contains('quantity__input')) return;
    
    // Atualiza o preço visualmente
    this.updateItemPrice(event.target);
    // Envia ao servidor com debounce
    this.debouncedServerUpdate(
      event.target.dataset.index, 
      event.target.value, 
      document.activeElement.getAttribute('name'), 
      event.target.dataset.quantityVariantId
    );
  }

  handleInput(event) {
    if (!event.target.classList.contains('quantity__input')) return;
    // Atualiza apenas a UI no input (sem enviar ao servidor)
    this.updateItemPrice(event.target);
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-drawer') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  // Formata o preço em centavos para o formato de moeda
  formatMoney(cents) {
    // Formato brasileiro: R$ X.XXX,XX
    const value = (cents / 100).toFixed(2);
    const parts = value.split('.');
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `R$ ${integerPart},${parts[1]}`;
  }

  // Atualiza o preço do item baseado na quantidade
  updateItemPrice(input) {
    const cartItem = input.closest('.cart-item');
    if (!cartItem) return;

    const quantity = parseInt(input.value, 10) || 0;
    const unitPrice = parseInt(input.dataset.unitPrice, 10) || 0;
    const originalUnitPrice = parseInt(input.dataset.originalUnitPrice, 10) || 0;
    const hasDiscount = input.dataset.hasDiscount === 'true';

    const priceWrapper = cartItem.querySelector('.cart-item__price-wrapper');
    if (!priceWrapper) return;

    const newLinePrice = unitPrice * quantity;
    const newOriginalLinePrice = originalUnitPrice * quantity;

    if (hasDiscount) {
      // Produto com desconto
      const finalPriceEl = priceWrapper.querySelector('.cart-item__final-price');
      const oldPriceEl = priceWrapper.querySelector('.cart-item__old-price');
      
      if (finalPriceEl) {
        finalPriceEl.textContent = this.formatMoney(newLinePrice);
      }
      if (oldPriceEl) {
        oldPriceEl.textContent = this.formatMoney(newOriginalLinePrice);
      }
    } else {
      // Produto sem desconto
      const regularPriceEl = priceWrapper.querySelector('.cart-item__regular-price');
      if (regularPriceEl) {
        regularPriceEl.textContent = this.formatMoney(newLinePrice);
      }
    }

    // Atualiza também o total do carrinho (estimativa)
    this.updateCartTotal();
  }

  // Atualiza o total estimado do carrinho
  updateCartTotal() {
    const inputs = this.querySelectorAll('.quantity__input');
    let total = 0;

    inputs.forEach(input => {
      const quantity = parseInt(input.value, 10) || 0;
      const unitPrice = parseInt(input.dataset.unitPrice, 10) || 0;
      total += unitPrice * quantity;
    });

    const totalElement = document.querySelector('.cart-drawer__total-value');
    if (totalElement) {
      totalElement.textContent = this.formatMoney(total);
    }

    // Atualiza contagem de itens
    let itemCount = 0;
    inputs.forEach(input => {
      itemCount += parseInt(input.value, 10) || 0;
    });

    const countElement = document.querySelector('.cart-drawer__items-count');
    if (countElement) {
      countElement.textContent = `Itens no seu carrinho: (${itemCount})`;
    }
  }

  onCartUpdate() {
    fetch(`${routes.cart_url}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.cart-drawer__warnings', '.drawer__heading__container', '.free-shipping-message'];
        for (const selector of selectors) {
          const targetElement = document.querySelector(selector);
          const sourceElement = html.querySelector(selector);
          if (targetElement && sourceElement) {
            const adoptedNode = document.adoptNode(sourceElement);
            targetElement.replaceWith(adoptedNode);
          }
        }

        const cartDrawer = document.querySelector('cart-drawer');
        const sourceCartDrawer = html.querySelector('cart-drawer');
        if (cartDrawer && sourceCartDrawer) {
          cartDrawer.className = sourceCartDrawer.className;
        }

        // Reinicializa o Disco.js no free-shipping-message atualizado
        const freeShippingMessage = document.querySelector('.free-shipping-message');
        if (cartDrawer && typeof cartDrawer.reinitializeDisco === 'function') {
          cartDrawer.reinitializeDisco(freeShippingMessage);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionDOM(html, selector) {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('CartDrawer') || document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);
    const cartItems = [...cartItemElements];

    cartItems.forEach((overlay) => overlay.classList.add('active'));

    document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('CartDrawer') || document.getElementById('main-cart-items');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading-overlay`);
    const cartItems = [...cartItemElements];

    cartItems.forEach((overlay) => overlay.classList.remove('active'));
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }})
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement = document.getElementById(`Drawer-quantity-${line}`) || document.getElementById(`Quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section => {
          // Tratamento especial para cart-icon-bubble: atualiza innerHTML para preservar o <a>
          if (section.id === 'cart-icon-bubble') {
            const targetElement = document.getElementById(section.id);
            if (targetElement && parsedState.sections[section.section]) {
              const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
              const sourceContent = html.querySelector('.shopify-section');
              if (sourceContent) {
                targetElement.innerHTML = sourceContent.innerHTML;
              }
            }
            return;
          }
          
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          
          // Usa document.adoptNode para garantir que Custom Elements sejam upgraded
          const newContent = this.getSectionDOM(parsedState.sections[section.section], section.selector);
          if (newContent) {
            const adoptedNode = document.adoptNode(newContent);
            elementToReplace.replaceWith(adoptedNode);
          }
        }));
        
        // Update warnings element when cart becomes empty
        if (parsedState.item_count === 0) {
          const warningsElement = document.querySelector('.cart-drawer__warnings');
          if (warningsElement) {
            warningsElement.classList.add('active');
          }
          // Limpa o localStorage quando o carrinho fica vazio
          CartStorage.clear();
        }
        
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem = document.getElementById(`CartDrawer-Item-${line}`) || document.getElementById(`CartItem-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`)) : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'))
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'))
        }

        // Reinicializa o Disco.js no free-shipping-message atualizado
        const freeShippingMessage = document.querySelector('.free-shipping-message');
        if (cartDrawerWrapper && typeof cartDrawerWrapper.reinitializeDisco === 'function') {
          cartDrawerWrapper.reinitializeDisco(freeShippingMessage);
        }
        
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'cart-drawer'});
      }).catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError = document.getElementById(`CartDrawer-LineItemError-${line}`) || document.getElementById(`Line-item-error-${line}`);
    if (lineItemError) {
      const errorText = lineItemError.querySelector('.cart-item__error-text');
      if (errorText) errorText.innerHTML = message;
    }

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    if (cartStatus) {
      cartStatus.setAttribute('aria-hidden', false);

      setTimeout(() => {
        cartStatus.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
